<script>
import { defineAsyncComponent } from 'vue';
import "@common/syncfusionCss";
import { configStore } from "@common/configStore";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import headerImage from '@assets/img/Samaritan-header-mobile.png';
import { controlStore, createStoreIndex } from "@common/control/controlStore";
import { vmsIcons } from "@common/icon/iconRegistry";
import { ddStore } from "@common/ddStore";
import { navigationStore } from "@common/navigation/navigationStore";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

const editorSettings = {};

let vmsCfg = {
    cId: "vms",
    cType: "vms",
    cTitle: "VMS",
    cTemplate: `<div v-if="!ddLoading" oncontextmenu="return false" class="main-grid-wrapper">
                    <ejs-sidebar
                        id="rightSidebar"
                        ref="rightSidebar"
                        :width="'300px'"
                        :enableDock='true'
                        :position="'Right'"
                        :dockSize="'0px'"
                        :enableGestures="false"
                        :isOpened="rightPanelOpened"
                        :target="'.main-grid-main-content-wrapper'"
                        type="Over"
                        @open="rightPanelOpened = !rightPanelOpened"
                        @close="onRightSidebarClose"
                    >
                        <navigation-views v-if="activeRightPanel === 'NAV_HISTORY'" type="history"></navigation-views>
                        <navigation-views v-else-if="activeRightPanel === 'NAV_MINIMIZED'" type="min"></navigation-views>
                    </ejs-sidebar>
                    <header class="vms-header">
                        <img :src="headerImage" alt="Samaritan" class="header-image" />
                        <div class="vms-header-spacer"></div>
                        <global-hierarchy-filters c-id="globalHierarchyFilters"></global-hierarchy-filters>
                        <div class="separator"></div>
                        <control name="HELP_MENU" :items="helpMenu"></control>
                        <div class="separator"></div>
                        <control name="USER_MENU" :items="userMenu" :label="userNameLabelTemplate"></control>
                    </header>
                    <div class="main-grid-main-content-wrapper">
                        <div class="nav-new-left">
                            <div class="nav-new-icons">
                                <div v-if="!leftPanelOpened">
                                    <control name="EXPAND_LEFT_PANEL"></control>
                                </div>
                                <div v-for="(control, index) in leftMenu" :key="index" class="nav-new-icon-btn" :class="{'active': control.name === 'COORD_' + entity?.toUpperCase?.()}">
                                    <control :name="control.name"></control>
                                </div>
                            </div>
                        </div>
                        <div class="vms-nav-drawer-region" :class="{'open': leftPanelOpened && activeNavItem}">
                            <nav-drawer
                                :nav-item="activeNavItem"
                                :has-drawer-content="!!activeDrawerButton"
                                @close="closeDrawer"
                            >
                                <template v-if="activeDrawerButton === 'ADMIN_OPT'" #drawer-content>
                                    <div class="drawer-admin-list">
                                        <button v-for="item in adminDrawerItems" :key="item.value" class="drawer-admin-item" @click="doAction({ item: item })">{{ item.text }}</button>
                                    </div>
                                </template>
                            </nav-drawer>
                        </div>
                        <div class="main-grid-content" v-if="!reloading">
                            <vms-main-grid
                                v-if="psp && psp !== 'dashboard'"
                                :c-id="'vmsMainGrid__' + entity"
                                :prop-psp="psp"
                                @close-panel="leftPanelOpened = false"
                                ref="vmsMainGrid"
                            ></vms-main-grid>
                            <layout-container v-if="psp === 'dashboard'" :c-id="accountSettings.ACCOUNT_EC_DASHBOARD_ID"></layout-container>
                        </div>
                        <div class="main-grid-right-sidebar-toggle">
                            <control v-show="!rightPanelOpened" name="EXPAND_RIGHT_PANEL"></control>
                            <control v-show="rightPanelOpened" name="COLLAPSE_RIGHT_PANEL"></control>
                            <control name="NAV_HISTORY"></control>
                            <control name="NAV_MINIMIZED"></control>
                        </div>
                    </div>
                </div>
                `,
    controls: [
        {
            name: 'HELP_MENU',
            type: 'dropdownbutton',
            iconCss: 'fa-regular fa-circle-question',
            cssClass: 'e-caret-hide',
            width: '28px',
            onselect: 'doAction'
        },
        {
            name: 'USER_MENU',
            type: 'dropdownbutton',
            width: 'fit-content',
            onbeforeitemrender: 'onBeforeItemRender',
            onselect: 'doAction',
            hideTitle: true
        },
        {
            name: 'EXPAND_RIGHT_PANEL',
            label: 'Expand Right Panel',
            type: 'button',
            cssClass: 'e-flat',
            onclick: 'toggleRightButtonPanel',
            hideLabel: true,
            icon: {
                name: vmsIcons['EXPAND_RIGHT_PANEL'],
                size: "base"
            }
        },
        {
            name: 'COLLAPSE_RIGHT_PANEL',
            label: 'Collapse Right Panel',
            type: 'button',
            cssClass: 'e-flat',
            onclick: 'toggleRightButtonPanel',
            hideLabel: true,
            icon: {
                name: vmsIcons['COLLAPSE_RIGHT_PANEL'],
                size: "base"
            }
        },
        {
            name: 'NAV_HISTORY',
            label: 'Navigation History',
            type: 'button',
            cssClass: 'e-flat nav-new-icon-btn',
            onclick: 'openRightPanel',
            hideLabel: true,
            icon: {
                name: vmsIcons['NAV_HISTORY'],
                size: "base"
            }
        },
        {
            name: 'NAV_MINIMIZED',
            label: 'Minimized Windows',
            type: 'button',
            cssClass: 'e-flat nav-new-icon-btn',
            onclick: 'openRightPanel',
            hideLabel: true,
            icon: {
                name: vmsIcons['NAV_MINIMIZED'],
                size: "base"
            }
        }
    ]
};

const vmsCmp = {
    name: 'vms',
    preInitControls: true,
    editorSettings: editorSettings,
    props: {
        entity: {
            default: false
        },
        entityId: {
            default: false
        }
    },
    data() {
        return {
            leftMenu: [],
            userMenu: [],
            helpMenu: [],
            adminDrawerItems: [],
            activeDrawerButton: null,
            activeRightPanel: null,
            headerImage: headerImage,
            leftPanelOpened: false,
            rightPanelOpened: false,
            selectedTab: false,
            reloading: false,
            ddLoading: true,
            initialsHeaderColors: [
                '#245386',
                '#DB2E2F',
                '#3B855D',
                '#6E3481',
                '#F19E41',
                '#FF4CA4',
                '#D5A300'
            ]
        };
    },
    computed: {
        psp() {
            return library.getPspByAbbrev(this.entity);
        },
        activeNavItem() {
            if (this.activeDrawerButton) {
                return this.leftMenu.find((item) => item.name === this.activeDrawerButton) || null;
            }
            if (!this.entity) return null;
            return this.leftMenu.find((item) => item.name === 'COORD_' + this.entity?.toUpperCase()) || null;
        },
        userNameLabelTemplate() {
            if (typeof this.userStore.coordinator !== 'object') {
                return '';
            }

            let clientName = null;
            let clientInitials = null;
            let clientID = null;
            let clientEmail = null;
            let initialsColor = null;

            if (
                typeof this.userStore.coordinator.CLIENT_NAME === 'string'
                && this.userStore.coordinator.CLIENT_NAME
            ) {
                clientName = this.userStore.coordinator.CLIENT_NAME;
            } else if (
                typeof this.userStore.coordinator.CLIENT_USER_NAME === 'string'
                && this.userStore.coordinator.CLIENT_USER_NAME
            ) {
                clientName = this.userStore.coordinator.CLIENT_USER_NAME;
            }

            if (
                typeof this.userStore.coordinator.CLIENT_ID === 'number'
                && this.userStore.coordinator.CLIENT_ID
            ) {
                clientID = this.userStore.coordinator.CLIENT_ID;
            }

            if (
                typeof this.userStore.coordinator.CLIENT_EMAIL === 'string'
                && this.userStore.coordinator.CLIENT_EMAIL
            ) {
                clientEmail = this.userStore.coordinator.CLIENT_EMAIL;
            }

            clientInitials = (clientName?.trim().split(/\s+/).slice(0, 2))
                .map((name) => name[0].toUpperCase())
                .join('');

            if (this.initialsHeaderColors.length && clientID) {
                initialsColor = this.initialsHeaderColors[clientID % this.initialsHeaderColors.length];
            }

            return `
                <div class="header-user-menu">
                    <div class="user-initials" style="` + (initialsColor ? 'background: ' + initialsColor : '') + `">
                        <span>` + clientInitials + `</span>
                    </div>
                    <div class="user-labels">
                        <div class="user-name"><span>` + clientName + `</span></div>
                        ` + (clientEmail ? `<div class="user-email"><span>` + clientEmail + `</span></div>` : ``) + `
                    </div>
                </div>
            `;
        },
    },
    methods: {
        processTab(ev, button) {
            if (button?.name?.indexOf('COORD_') === -1) return;
            let tmpPspAbbrev = button?.name?.replace('COORD_', '');
            if (!tmpPspAbbrev) return;
            let goTo = '/' + tmpPspAbbrev.toLowerCase();
            if (this.$router.currentRoute.value.path === goTo) {
                this.toggleLeftButtonPanel();
                return;
            }
            this.reloading = true;
            this.$nextTick(() => {
                this.$router.push({ path: '/' + tmpPspAbbrev.toLowerCase(), query: this.$router.currentRoute.value.query });
                setTimeout(() => { this.reloading = false; }, 500);
            });
        },
        toggleLeftButtonPanel() {
            this.leftPanelOpened = !this.leftPanelOpened;
        },
        openSpecialDrawer(ev, button) {
            if (this.activeDrawerButton === button.name && this.leftPanelOpened) {
                this.closeDrawer();
                return;
            }
            this.activeDrawerButton = button.name;
            this.leftPanelOpened = true;
        },
        closeDrawer() {
            this.leftPanelOpened = false;
            this.activeDrawerButton = null;
        },
        openRightPanel(ev, button) {
            if (this.activeRightPanel === button.name && this.rightPanelOpened) {
                this.$refs['rightSidebar']?.ej2Instances?.hide?.();
                return;
            }
            this.activeRightPanel = button.name;
            if (!this.rightPanelOpened) {
                this.$refs['rightSidebar']?.ej2Instances?.show?.();
            }
        },
        onRightSidebarClose() {
            this.rightPanelOpened = !this.rightPanelOpened;
            this.activeRightPanel = null;
        },
        toggleRightButtonPanel() {
            if (this?.$refs?.['rightSidebar']?.ej2Instances?.toggle) this.$refs['rightSidebar'].ej2Instances.toggle();
        },
        async doAction(ev) {
            if (!ev?.item?.itemAction) return;
            let act = ev.item.itemAction;
            if (act === "'about'") {
                actionStore.openAbout();
            } else if (act === "logout") {
                actionStore.logout({ psp: 'user' });
            } else if (navigationStore().findPath(act)) {
                let viewConfig = {
                    windowName: act,
                    perspectiveID: this.psp
                };
                if (act === 'AdminProfile') {
                    viewConfig.windowName = act + ev.item.itemIndex;
                    viewConfig.activeTab = ev.item.itemIndex;
                }
                navigationStore().open(viewConfig);
            }
        },
        async onLoad() {
            this.ddLoading = true;
            await ddStore.preloadGroup('COMMON');
            await ddStore.preloadGroup('DATA_GRID');
            this.ddLoading = false;
            this.ffList = await configStore.getAllClientFFList();
            let addControl = (controlToAdd, disabled) => {
                let tmpControl = {};
                let isDropdownButton = controlToAdd?.items?.length || controlToAdd?.isDropdownButton;
                tmpControl.type = isDropdownButton ? 'dropdownbutton' : 'button';
                if (isDropdownButton) {
                    tmpControl.items = controlToAdd.items;
                    tmpControl.fields = { text: 'text', value: 'value' };
                    tmpControl.caretCss = 'hide';
                    tmpControl.onselect = 'doAction';
                    if (controlToAdd?.targetComponent) tmpControl.targetComponent = controlToAdd.targetComponent;
                }
                tmpControl.label = controlToAdd.text;
                tmpControl.hideLabel = true;
                tmpControl.title = controlToAdd.text;
                tmpControl.name = controlToAdd.value;
                tmpControl.disabled = disabled || false;
                tmpControl.cssClass = 'e-flat nav-new-icon-btn';
                if (!disabled) tmpControl.onclick = 'processTab';
                if (controlToAdd.onclick) tmpControl.onclick = controlToAdd.onclick;
                if (vmsIcons[tmpControl.name]) {
                    tmpControl.icon = {
                        name: vmsIcons[tmpControl.name],
                        size: "2xl"
                    };
                }
                let controlStoreIndex = createStoreIndex(this.storeId, tmpControl.name);
                if (typeof this.controlConfigs === 'undefined') this.controlConfigs = {};
                this.controlConfigs[tmpControl.name] = tmpControl;
                controlStore().addControl(tmpControl, this, controlStoreIndex);
                return tmpControl;
            };
            addControl({
                text: 'Expand',
                value: 'EXPAND_LEFT_PANEL',
                onclick: 'toggleLeftButtonPanel'
            });
            let excludeLeftMenu = ['COORD_GLOBAL_FILTERS', 'COORD_PRO'];
            let dashboardId = this.accountSettings['ACCOUNT_EC_DASHBOARD_ID'];
            if (!dashboardId) {
                excludeLeftMenu.push('COORD_DASHBOARD');
            }
            let selectedPspIsFound = false;

            let availableNavigations = navigationStore().availableNavigations;
            if (availableNavigations?.menu) {
                let tmpUserMenu = availableNavigations.menu.find((tmp) => tmp.name === 'M_ACCOUNT');
                if (Array.isArray(tmpUserMenu?.items)) {
                    let tmpItems = [];
                    tmpUserMenu.items.forEach((item) => {
                        tmpItems.push({
                            text: item.title,
                            value: item.name,
                            itemAction: item.action.act
                        });
                    });
                    this.userMenu = library.cloneObj(tmpItems);
                }
                let tmpHelpMenu = availableNavigations.menu.find((tmp) => tmp.name === 'M_HELP');
                // debugger;
                if (Array.isArray(tmpHelpMenu?.items)) {
                    let tmpItems = [];
                    tmpHelpMenu.items.forEach((item) => {
                        if (item.ITEM_URL.indexOf('/custom/') === -1 && item.ITEM_URL.indexOf('devdash') === -1) {
                            tmpItems.push({
                                text: item.ITEM_TITLE,
                                value: item.action,
                                itemAction: item.action,
                                itemUrl: item.ITEM_URL
                            });
                        } else {
                            // @todo add to the settings
                        }
                    });
                    this.helpMenu = library.cloneObj(tmpItems);
                }
            }
            if (availableNavigations?.tabs) {
                for (let i in availableNavigations.tabs) {
                    if (excludeLeftMenu.indexOf(availableNavigations.tabs[i].FF_CONSTANT) === -1) {
                        if (!selectedPspIsFound) {
                            if (this.psp && availableNavigations.tabs[i].FF_CONSTANT === ('COORD_' + this.psp).toUpperCase()) {
                                selectedPspIsFound = this.psp;
                            }
                            if (!this.psp && availableNavigations.tabs[i].FF_CONSTANT.indexOf('COORD_') !== -1) {
                                selectedPspIsFound = availableNavigations.tabs[i].FF_CONSTANT.replace('COORD_', '');
                            }
                        }
                        let tmpControl = {
                            value: availableNavigations.tabs[i].FF_CONSTANT,
                            text: availableNavigations.tabs[i].FF_NAME
                        };
                        this.leftMenu.push(addControl(tmpControl));
                    }
                }
            }
            if (availableNavigations?.adminTabs) {
                let tmpControl = {
                    text: 'Administrative Options',
                    value: 'ADMIN_OPT',
                    onclick: 'openSpecialDrawer'
                };
                let items = [];
                for (let i in availableNavigations.adminTabs) {
                    if (excludeLeftMenu.indexOf(availableNavigations.adminTabs[i].FF_CONSTANT) === -1) {
                        items.push({
                            value: availableNavigations.adminTabs[i].FF_CONSTANT,
                            text: availableNavigations.adminTabs[i].FF_NAME,
                            itemAction: 'AdminProfile',
                            itemIndex: i
                        });
                    }
                }
                if (items.length) {
                    this.adminDrawerItems = library.cloneObj(items);
                    this.leftMenu.push(addControl(tmpControl));
                }
            }

            if (!this.psp && selectedPspIsFound) {
                this.$router.push({ path: '/' + selectedPspIsFound.toLowerCase() });
            }
            navigationStore().restoreUrl();
        },

    },
    components: {
        menuBar: defineAsyncComponent(() => import('@common/menuBar.vue')),
        'nav-drawer': defineAsyncComponent(() => import('./NavDrawer.vue')),
        'ejs-sidebar': defineAsyncComponent(() => import('@common/syncfusion/sidebar.vue')),
        'ejs-tooltip': defineAsyncComponent(() => import('@common/syncfusion/tooltip')),
        'ejs-dropdownbutton': defineAsyncComponent(() => import('@common/syncfusion/dropdownButton')),
        'navigation-views': defineAsyncComponent(() => import('@common/navigation/navigationViews')),
    }
};

componentStore.initComponent(vmsCmp, vmsCfg);
export { vmsCfg, vmsCmp };
export default vmsCmp;
</script>

<style lang="css">
@import "~@common/global.css";

.vms-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    min-height: 3.75rem;
    background-color: var(--color-surface);
    border-bottom: var(--border-default);
    box-shadow: var(--shadow-sm);
    padding: var(--space-1) var(--space-11);
    flex-shrink: 0;
    overflow: hidden;
}
.vms-header-spacer {
    flex-grow: 1;
}
</style>

<style scoped>
:deep(.main-grid-wrapper) {
    display: flex;
    flex-direction: column;
    height: 100vh;
    overflow: hidden;
}
:deep(.main-grid-main-content-wrapper) {
    display: flex;
    flex-grow: 12;
    overflow: hidden;
    min-height: 0;
}
:deep(.header-image) {
    height: 40px;
    width: auto;
    object-fit: contain;
}
:deep(.panel) {
    padding: 10px;
}
:deep(.panel button) {
    text-align: left;
    text-transform: none;
    font-weight: 500;
    font-size: 13px;
    color: rgb(67, 67, 67);
}
:deep(.panel button .e-btn-icon) {
    font-size: 20px;
    color: rgb(67, 67, 67);
    width: 30px;
    margin-left: 0;
    text-align: center;
    font-weight: 300;
}
:deep(.panel-section) {
    /*margin-bottom: 30px;*/
}

:deep(.panel-section > div) {
    margin-top: 5px;
    margin-bottom: 5px;
}

:deep(.panel-section h4) {
    /*font-size: 1.1em;*/
    margin: 0 0 10px 0;
    color: #959595;
}
:deep(.toggle-panel) {
    width: 20px;
    height: 20px;
    line-height: 20px;
    font-size: 22px;
    font-weight: 300;
    color: #959595;
    position: absolute;
    bottom: 10px;
    margin: 0 auto;
    display: block;
    left: 0;
    right: 0;
}

:deep(.toggle-panel.opened) {
    right: 10px;
    margin: unset;
    left: unset;
}

:deep(hr.panel-separator) {
    border: none;
    height: 1px;
    background-color: #959595;
    margin: 20px 0;
}
:deep(.main-grid-content) {
    flex-grow: 12;
    overflow: hidden;
    min-height: 0;
    height: 100%;
}
:deep(.main-grid-content > div) {
    height: 100%;
}
:deep(.main-grid-content button) {
    text-transform: none;
}
:deep(.nav-new-left) {
    width: 70px;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px 0;
    background-color: #f8f9fa;
    overflow-y: auto;
    overflow-x: hidden;
}

:deep(.nav-new-expand-btn) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    border: none;
    background: none;
    color: #6b7280;
    cursor: pointer;
    transition: background-color 0.15s ease,
    color 0.15s ease;
    position: relative;
}

:deep(.nav-new-expand-btn:hover:not(:disabled)) {
    background-color: #e5e7eb;
    color: #111827;
}

:deep(.nav-new-expand-btn:disabled) {
    opacity: 0.4;
    cursor: default;
}

:deep(.nav-new-expand-btn:hover:not(:disabled) .nav-new-expand-tooltip) {
    opacity: 1;
    transform: translateX(0);
}

:deep(.nav-new-icon-btn) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 999px;
    border: none;
    background: none;
    color: #495057;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

:deep(.nav-new-icon-btn:hover) {
    background-color: #e9ecef;
    color: #213547;
}

:deep(.nav-new-icon-btn.active) {
    background-color: #213547;
}

:deep(.nav-new-icon-btn.active i) {
    color: var(--color-text-inverse) !important;
}

:deep(.nav-new-icon-btn:hover .nav-new-tooltip) {
    opacity: 1;
    transform: translateX(0);
}
:deep(.nav-new-icons) {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
:deep(.main-grid-right-sidebar-toggle) {
    width: 3rem;
    background-color: var(--color-background);
    border-left: var(--border-default);
    padding: var(--space-3) 0;
    flex-shrink: 0;
    z-index: 1001;
    overflow-y: auto;
    overflow-x: hidden;
}

:deep(#rightSidebar) {
    right: 3rem;
    background-color: var(--color-background);
}
:deep(.separator:before) {
    content: var(--pipe-separator);
    color: var(--color-text-muted);
    margin: 0 var(--space-4);
}
:deep(.vms-nav-drawer-region) {
    width: 0;
    overflow: hidden;
    flex-shrink: 0;
    transition: width 0.25s ease;
    display: flex;
    flex-direction: column;
    background-color: #ffffff;
    border-left: 1px solid #e0e0e0;
    box-shadow: 2px 0 8px rgba(15, 23, 42, 0.06);
}
:deep(.vms-nav-drawer-region.open) {
    width: 265px;
}

:deep(.drawer-admin-list) {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

:deep(.drawer-admin-item) {
    display: flex;
    align-items: center;
    width: 100%;
    padding: 0.5rem 0.75rem;
    border: none;
    background: none;
    cursor: pointer;
    text-align: left;
    font-size: 0.875rem;
    color: #111827;
    border-radius: 6px;
    transition: background-color 0.15s ease;
}

:deep(.drawer-admin-item:hover) {
    background-color: #f3f4f6;
}
</style>
